import FileRow from "components/file-components/file-row";
import EditIcon from "components/icon/EditIcon";
import ReadMoreText from "components/ReadMoreText";
import { AssetAppeal, AssetAppealRequestActionStatusEnum } from "types";
import AssetAppealCoordinatorActions from "./AssetAppealCoordinatorActions";
import AssetAppealActions from "components/button/AssetAppealActions";
import AppealCourtInfoCard from "components/card/AppealCourtCard";
import { getInitials, getAvatarBgColor } from "utils/helper";

const AssetAppealDisplay: React.FC<{
  appeal?: AssetAppeal;
  fileRowProps?: any;
  onEditClick?: () => void;
  isAssetProsecutor?: boolean;
  handleAppealApproval?: () => void;
  setIsRespondBackAssetAppealOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  isAssetCoordinator?: boolean;
  isApproving?: boolean;
  setIsAddLowerCourtPopup?: React.Dispatch<React.SetStateAction<boolean>>;
  hasLowerCourtData?: boolean;
  handleSubmitToCourt?: () => void;
  isTransfering?: boolean;
  setIsAddLoweerCourtPopup?: React.Dispatch<React.SetStateAction<boolean>>;
  hasHigherCourtData?: boolean;
  setIsProsecutorReassignPopupOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  hasProsecutor?: boolean;
  setIsAppealPopupOpen?: React.Dispatch<
    React.SetStateAction<{ open: boolean; data: any }>
  >;
  handleRespondentAppealForwardToCoordinator?: () => void;
  isAssetRegistrar?: boolean;
  isForwarding?: boolean;
}> = ({
  appeal,
  fileRowProps,
  onEditClick,
  isAssetProsecutor,
  handleAppealApproval,
  setIsRespondBackAssetAppealOpen,
  isAssetCoordinator,
  isApproving,
  setIsAddLowerCourtPopup,
  hasLowerCourtData,
  handleSubmitToCourt,
  isTransfering,
  setIsAddLoweerCourtPopup,
  hasHigherCourtData,
  setIsProsecutorReassignPopupOpen,
  hasProsecutor,
  handleRespondentAppealForwardToCoordinator,
  isAssetRegistrar,
  isForwarding,
}) => {
  const initials = getInitials(appeal?.assigned_prosecutor_name as string);
  const bgColor = getAvatarBgColor(appeal?.assigned_prosecutor_name as string);

  return (
    <>
      <div className="flex justify-end mb-2 text-left">
        <AssetAppealActions
          action_status={appeal?.action_status as string}
          appeal={appeal}
          onAddLowerCourtClick={setIsAddLowerCourtPopup}
          hasLowerCourtData={hasLowerCourtData}
          hasHigherCourtData={hasHigherCourtData}
          onAddHigherCourtClick={setIsAddLoweerCourtPopup}
          onAddProsecutorClick={setIsProsecutorReassignPopupOpen}
          isAssetCoordinator={isAssetCoordinator}
          hasProsecutor={hasProsecutor}
        />
      </div>
      {hasLowerCourtData && (
        <AppealCourtInfoCard
          court_info={"appeal?.lo " as string}
          court_reg={appeal?.lower_registration_number as string}
          date={appeal?.lower_registration_date as string}
          type={"Lower"}
          court_name={appeal?.lower_court_name}
          court_level={appeal?.lower_court_level}
          court_type={appeal?.lower_court_type as string}
          action_status={appeal?.action_status}
          hasLowerCourtData={hasLowerCourtData}
          onSubmitToCourt={handleSubmitToCourt}
          isTransfering={isTransfering}
        />
      )}
      {hasHigherCourtData && (
        <div className="mt-3">
          {" "}
          <AppealCourtInfoCard
            court_reg={appeal?.registration_number || ""}
            date={appeal?.registration_date || ""}
            type="Higher"
            court_name={appeal?.higher_court_name}
            court_level={appeal?.higher_court_level}
            court_type={appeal?.higher_court_type as string}
            court_appeal_number={appeal?.court_appeal_number}
            court_summons={appeal?.court_summons as File[]}
            opening_date={appeal?.opening_date}
            first_hearing_date={appeal?.first_hearing_date}
            hearing_date={appeal?.higher_court_hearing_date}
            action_status={appeal?.action_status}
            hasLowerCourtData={hasLowerCourtData}
            hasHigherCourtData={hasHigherCourtData}
            onSubmitToCourt={handleSubmitToCourt}
            isTransfering={isTransfering}
            fileRowProps={fileRowProps}
          />
        </div>
      )}
      <div className="flex justify-between items-center mt-3">
        <h3 className="text-lg font-semibold mb-4">Appeal Information</h3>
        {isAssetProsecutor &&
          (appeal?.action_status ===
            AssetAppealRequestActionStatusEnum.APPEAL_REQUESTED ||
            appeal?.action_status ===
              AssetAppealRequestActionStatusEnum.APPEAL_RESPONDED) && (
            <button
              type="button"
              onClick={onEditClick}
              className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors"
            >
              <EditIcon />
              Edit
            </button>
          )}
      </div>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span style={{ color: "#432101AB" }}>Status</span>
          <span
            className="font-medium"
            style={{ color: " var(--Prime-Blue, #0A1D39)" }}
          >
            {appeal?.action_status || ""}
          </span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: "#432101AB" }}>Appeal Type</span>
          <span style={{ color: " var(--Prime-Blue, #0A1D39)" }}>
            {appeal?.appeal_type || "—"}
          </span>
        </div>

        <div className="flex justify-between">
          <span style={{ color: "#432101AB" }}>Date Submitted</span>
          <span style={{ color: " var(--Prime-Blue, #0A1D39)" }}>
            {appeal?.date_submitted || "—"}
          </span>
        </div>

        <div className="flex justify-between">
          <span style={{ color: "#432101AB" }}>Requesting Prosecutor</span>
          <span style={{ color: " var(--Prime-Blue, #0A1D39)" }}>
            {appeal?.requesting_prosecutor_name || "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span style={{ color: "#432101AB" }}>Claim Summary</span>
          <div style={{ color: "var(--Prime-Blue, #0A1D39)" }}>
            {appeal?.appeal_claim_summary ? (
              <ReadMoreText text={appeal?.appeal_claim_summary} maxLines={3} />
            ) : (
              "—"
            )}
          </div>
        </div>
        {appeal?.asset &&
          ((Array.isArray(appeal.asset) && appeal.asset.length > 0) ||
            (!Array.isArray(appeal.asset) &&
              typeof appeal.asset === "object")) && (
            <div className="flex justify-between flex-col">
              <span style={{ color: "#432101AB" }}>Associated Asset</span>

              {Array.isArray(appeal.asset)
                ? appeal.asset.length
                  ? appeal.asset.map((asset) => (
                      <li
                        key={asset?.asset_unique_id}
                        className="ml-5"
                        style={{ color: " var(--Prime-Blue, #0A1D39)" }}
                      >
                        {asset?.asset_unique_id}{" "}
                      </li>
                    ))
                  : "—"
                : (appeal.asset as { asset_unique_id?: string })
                    ?.asset_unique_id ?? "—"}
            </div>
          )}
        {/* {referral?.(<div className="flex justify-between">
          <span style={{ color: "#432101AB" }}>Date</span>
          <span style={{ color: " var(--Prime-Blue, #0A1D39)" }}>
            {appeal?.date || "—"}
          </span>
        </div>)} */}
        {appeal?.appeal_documents && appeal?.appeal_documents?.length > 0 && (
          <div className="flex flex-col">
            <span style={{ color: "#432101AB" }}>Appeal Documents</span>
            <div style={{ color: "var(--Prime-Blue, #0A1D39)" }}>
              {appeal?.appeal_documents.map((file, index) => (
                <FileRow file={file} key={index} {...fileRowProps} />
              ))}
            </div>
          </div>
        )}

        {appeal?.assigned_prosecutor_name && (
          <div className="flex gap-2  mt-4 flex-col">
            <p className="text-base font-semibold text-gray-800">
              Assigned Prosecutor
            </p>
            <div className="flex justify-between items-center border rounded-lg">
              <div className="flex items-center gap-2 p-3 ">
                {" "}
                <div
                  className={`w-10 h-10 rounded-full flex items-center
                         justify-center text-white text-lg font-semibold flex-shrink-0 mr-3 ${bgColor}`}
                >
                  {initials}
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    {appeal?.assigned_prosecutor_name || ""}
                  </p>
                  <p className="text-sm text-gray-500">Prosecutor</p>
                </div>
              </div>
              {isAssetCoordinator && (
                <button
                  className="mr-2"
                  type="button"
                  onClick={() => setIsProsecutorReassignPopupOpen?.(true)}
                >
                  <EditIcon />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {isAssetCoordinator &&
        (appeal?.action_status ===
          AssetAppealRequestActionStatusEnum.APPEAL_REQUESTED ||
          appeal?.action_status ===
            AssetAppealRequestActionStatusEnum.APPEAL_UPDATED) && (
          <AssetAppealCoordinatorActions
            handleAppealApproval={handleAppealApproval}
            setIsRespondBackAssetAppealOpen={setIsRespondBackAssetAppealOpen}
            isApproving={isApproving}
          />
        )}
    </>
  );
};

export default AssetAppealDisplay;
